/**
 * Rider → today's village route (Location Service Engine ``route_batching``): the dispatched batch's stops in
 * visiting order — re-sequenced from the rider's live position when Phase 2 live routing is on — with a
 * "reached local hub" step. Each stop opens the normal order screen (pickup / deliver / proof unchanged).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, MapPin, Navigation, RefreshCcw, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "../../lib/api";
import { useLse } from "../../lib/lse";

export default function RiderRoute() {
    const { has } = useLse();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = () => {
        const done = (pos) => {
            const qs = pos ? `?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}` : "";
            api.get(`/rider/lse/route${qs}`).then(({ data: d }) => setData(d)).catch((e) => { setData({ batches: [] }); toast.error(e?.response?.data?.detail || "Couldn't load your route"); });
        };
        if (has("live_routing") && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(done, () => done(null), { enableHighAccuracy: true, timeout: 8000, maximumAge: 20000 });
        } else done(null);
    };
    useEffect(() => { if (has("route_batching")) load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
    if (!has("route_batching")) return <div className="p-6 text-center text-sm text-muted-foreground">Village routes aren't enabled. <Link to="/rider" className="text-primary font-semibold">Back</Link></div>;

    const hub = async (b) => {
        setBusy(true);
        try { await api.post(`/rider/lse/batches/${b.id}/hub-reached`); toast.success("Customers told you're at the local hub"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };
    const liveStops = data?.live?.stops;

    return (
        <div className="bizil-rider max-w-md mx-auto pb-24" data-testid="rider-route-page">
            <header className="px-4 py-4 flex items-center gap-3 bg-card border-b border-border sticky top-0 z-10">
                <Link to="/rider" className="p-1 -ml-1" aria-label="Back"><ArrowLeft size={22} /></Link>
                <h1 className="font-bold flex-1">Today's route</h1>
                <button type="button" onClick={load} aria-label="Refresh" className="p-2"><RefreshCcw size={18} /></button>
            </header>
            {!data && <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>}
            {data && !data.batches?.length && !data.live?.stops?.length && <p className="p-6 text-center text-sm text-muted-foreground" data-testid="rider-route-empty">No route assigned to you today.</p>}
            {liveStops?.length > 0 && (
                <section className="m-4 rounded-2xl border border-border bg-card p-4" data-testid="rider-live-route">
                    <p className="text-sm font-bold inline-flex items-center gap-1.5"><Navigation size={15} className="text-primary" /> Best order from where you are</p>
                    <p className="text-xs text-muted-foreground">{data.live.total_km} km · about {data.live.total_minutes} min</p>
                    <ol className="mt-3 space-y-2">
                        {liveStops.map((s, i) => (
                            <li key={s.order_id}>
                                <Link to={`/rider/orders/${s.order_id}`} className="flex items-center gap-3 rounded-xl border border-border px-3 py-2">
                                    <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm font-semibold truncate">{s.customer_name || s.order_code}</span>
                                        <span className="block text-xs text-muted-foreground truncate">{s.village || s.address}</span>
                                    </span>
                                    <span className="text-xs text-muted-foreground shrink-0">{s.eta_minutes != null ? `~${s.eta_minutes} min` : "no pin"}</span>
                                </Link>
                            </li>
                        ))}
                    </ol>
                </section>
            )}
            {(data?.batches || []).map((b) => (
                <section key={b.id} className="m-4 rounded-2xl border border-border bg-card p-4" data-testid={`rider-batch-${b.id}`}>
                    <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                            <p className="font-bold truncate">{b.route_name}</p>
                            <p className="text-xs text-muted-foreground">{b.date} · {b.stops.length} stop(s) · {b.status.replace(/_/g, " ").toLowerCase()}</p>
                        </div>
                        {b.status === "DISPATCHED" && (
                            <button type="button" disabled={busy} onClick={() => hub(b)} data-testid={`rider-hub-${b.id}`}
                                    className="shrink-0 rounded-xl bg-foreground text-background text-xs font-bold px-3 min-h-[40px] inline-flex items-center gap-1 disabled:opacity-50">
                                <Warehouse size={14} /> Reached hub
                            </button>
                        )}
                    </div>
                    {!liveStops?.length && (
                        <ol className="mt-3 space-y-2">
                            {b.stops.map((o, i) => (
                                <li key={o.id}>
                                    <Link to={`/rider/orders/${o.id}`} className="flex items-center gap-3 rounded-xl border border-border px-3 py-2">
                                        <span className="w-7 h-7 rounded-full bg-muted text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-sm font-semibold truncate">{o.customer_name || o.order_unique_id}</span>
                                            <span className="block text-xs text-muted-foreground truncate"><MapPin size={10} className="inline" /> {o.route?.village_name || o.delivery_address?.line1}</span>
                                        </span>
                                        {o.status === "DELIVERED" ? <CheckCircle2 size={18} className="text-emerald-600" /> : <span className="text-xs font-semibold">{o.payment_method === "COD" ? inr(o.totals?.grand_total) : "Prepaid"}</span>}
                                    </Link>
                                </li>
                            ))}
                        </ol>
                    )}
                </section>
            ))}
        </div>
    );
}
